import {
    EditIcon,
    ElectronicsIcon,
    FlickerFreeIcon,
    PasswordResetIcon,
    TrashIcon,
    WacomTabletIcon
} from "../../../common/Icons";
import React, {useEffect, useMemo, useState} from "react";
import {
    Breadcrumbs,
    Button,
    Grid,
    IconButton, Paper,
    Skeleton,
    Stack,
    Switch,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import {EquipmentConnectionStatus} from "./components/EquipmentConnectionStatus";
import {checkPermissionsAndRedirect, hasPermission} from "../../../../configs/functions";
import {Add, Home, RemoveRedEye} from "@mui/icons-material";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useAppContext} from "../../../../contexts/AppContext";
import {useCommonItems} from "../../../../contexts/CommonItensProvider";
import {useSearchParams} from "react-router-dom";
import {
    atualizaEquipamento,
    carregaEquipamentos,
    carregaPontosEntrega, deletaEquipamento,
    generateUpdateTokenEquipamento
} from "./configs/functions";
import {listaEmpresas} from "../../management/companies/configs/functions";
import {Helmet} from "react-helmet-async";
import LinkNavegacao from "../../../common/Link";
import PageTitle from "../../../common/PageTitle";
import {FiltersButton} from "../../../common/FiltersButton";
import {EquipmentsTableFilter} from "./components/EquipmentsTableFilter";
import {CustomToolbarExportTable} from "../../../common/CustomToolbarExportTable";
import {SkeletonTableRow} from "../../../common/SkeletonTableRow";
import {EmptyTableRow} from "../../../common/EmptyTableRow";
import {Pagination} from "../../../common/Pagination";
import {DeskForm} from "./components/DeskForm";
import ConfirmDialog from "../../../common/ConfirmDialog";
import {ConfirmGenerateTokenDialog} from "./components/ConfirmGenerateTokenDialog";
import {GenerateTokenDialog} from "./components/GenerateTokenDialog";
import {LastSyncCell} from "./components/LastSyncCell";

const equipmentIcon = {
    "balcao": <WacomTabletIcon />,
    "virtual": <FlickerFreeIcon />,
}

const DeskRow = ({ data, isSempher, handleOpenEditForm, handleUpdateStatus, handleOpenDeleteDialog, handleOpenConfirmGenerateTokenDialog }) => {
    return (
        <TableRow>
            <TableCell align="center">
                {equipmentIcon[data.tipo]}
            </TableCell>
            <TableCell>{data.nome}</TableCell>
            <TableCell>{data.tag_identificacao}</TableCell>
            <TableCell>{data.empresa.nome}</TableCell>
            <TableCell>{data.descricao}</TableCell>

            {data.tipo === 'balcao' && (
                <TableCell>
                    <EquipmentConnectionStatus
                        mode={data.mode}
                        status={data.status}
                    />
                </TableCell>
            )}

            <TableCell>
                <LastSyncCell lastSyncAt={data.lastSyncAt} />
            </TableCell>
            <TableCell>
                <Switch checked={!!data.status} onChange={() => handleUpdateStatus('status', !data.status)} disabled={!data.status || !isSempher || !hasPermission(['admin', 'admin_equipamentos', 'update_equipamentos'])} />
            </TableCell>

            {data.tipo === 'balcao' && (
                <>
                    <TableCell>
                        <Switch disable checked={!!data.acesso_facial} onChange={() => handleUpdateStatus('acesso_facial', !data.acesso_facial)} disabled={!data.status || !isSempher} />
                    </TableCell>

                    <TableCell>
                        <Switch checked={!!data.acesso_retiradas} onChange={() => handleUpdateStatus('acesso_retiradas', !data.acesso_retiradas)} disabled={!data.status || !isSempher} />
                    </TableCell>
                </>
            )}

            <TableCell align="right" style={{ minWidth: 150 }}>
                {(isSempher && data.tipo === 'balcao') && (
                    <IconButton
                        aria-label="Gerar Token para Máquina"
                        onClick={() => handleOpenConfirmGenerateTokenDialog(data)}
                        disabled={!data.status}
                        sx={{ filter: !data.status ? "grayscale(1) brightness(1.1)" : "" }}
                    >
                        <PasswordResetIcon />
                    </IconButton>
                )}

                {isSempher ? (
                        <>
                            {hasPermission(["admin", "admin_equipamentos", "update_equipamentos"]) && (
                                <IconButton aria-label="Editar" onClick={() => handleOpenEditForm(data)}>
                                    <EditIcon />
                                </IconButton>
                            )}

                            {hasPermission(["admin", "admin_equipamentos", "delete_equipamentos"]) && (
                                <IconButton aria-label="Excluir" onClick={() => handleOpenDeleteDialog(data)}>
                                    <TrashIcon />
                                </IconButton>
                            )}
                        </>
                    ) :
                    <Button onClick={() => handleOpenEditForm(data)} size='small' color="primary" variant='contained' sx={{minWidth: 30}}>
                        <RemoveRedEye fontSize='small'/>
                    </Button>
                }
            </TableCell>
        </TableRow>
    )
}

export function ListOfDesks({ type = 'balcao' }) {
    const queryClient = useQueryClient();
    const { dadosUsuario } = useAppContext();
    const { exibirDialog, handleCloseDialog, exibirAlerta } = useCommonItems();

    const [searchParams, setSearchParams] = useSearchParams();

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openGenerateTokenDialog, setOpenGenerateTokenDialog] = useState(false);
    const [openConfirmGenerateTokenDialog, setOpenConfirmGenerateTokenDialog] = useState(false);

    const [openDeskForm, setOpenDeskForm] = useState(false);

    const [selectedEquipment, setSelectedEquipment] = useState(null);

    const [lastUpdated, setLastUpdated] = useState(null);

    const searchParamPage = searchParams.get('page');
    const searchParamPerPage = searchParams.get('perPage');

    const page = searchParamPage ? (parseInt(searchParamPage) - 1) : 0;
    const rowsPerPage = searchParamPerPage ? parseInt(searchParamPerPage) : 10;

    const [isOpenFilters, setIsOpenFilters] = useState(false);
    const [activeFilters, setActiveFilters] = useState(0);

    const tipo = type === 'balcao' ? 'Balcão' : 'Balcão Virtual';
    const tipoPlural = type === 'balcao' ? 'Balcões' : 'Balcões Virtuais';

    const columns = useMemo(() => {
        const base = [
            { field: 'tipo', headerName: 'Tipo' },
            { field: 'nome', headerName: 'Nome' },
            { field: 'tag_identificacao', headerName: 'Tag' },
            { field: 'empresa.fantasyName', headerName: 'Empresa', valueGetter: (params) => params.row.empresa?.nome },
            { field: 'descricao', headerName: 'Descrição' },
            { field: 'mode', headerName: 'Conexão', valueGetter: (params) => params.row.mode === 'unknown' ? 'desconhecida' : params.row.mode },
            { field: 'lastSyncAt', headerName: 'Última sincronização', valueGetter: (params) => params.row.lastSyncAt },
            { field: 'status', headerName: 'Status', valueGetter: (params) => params.row.status ? 'Ativado' : 'Inativado' },
        ];

        if (type !== 'balcao') {
            base.push(
                { field: 'acesso_facial', headerName: 'Habilitar facial', valueGetter: (params) => params.row.acesso_facial },
                { field: 'acesso_retiradas', headerName: 'Habilitar retirada', valueGetter: (params) => params.row.acesso_retiradas },
            );
        }

        return base;
    }, [type]);

    const queryFilters = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        nome: searchParams.get('nome'),
        id_empresas: [searchParams.get('id_empresas')],
        id_ponto_entrega: searchParams.get('id_ponto_entrega'),
        tipo: type === 'balcao' ? 'balcao' : 'virtual',
        status: searchParams.get('status'),
    };

    const queryKey = ['listaEquipamentos', queryFilters];
    const { data: equipamentosData, isLoading, isPending, isSuccess, isRefetching } = useQuery({
        queryKey,
        queryFn: () => carregaEquipamentos(queryFilters),
        refetchInterval: 1000 * 60 * 3, // 3 min
        refetchIntervalInBackground: true,
    });

    const equipamentos = equipamentosData?.data?.data ?? [];
    const numTotalItems = equipamentosData?.data?.numero_total ?? 0;
    const isLoadingList = isLoading || isPending;

    const { data: empresasData } = useQuery({
        queryKey: ['listaEmpresas'],
        queryFn: ({ queryKey }) => {
            const [_key, parametro] = queryKey;
            return listaEmpresas(null, null, null, true);
        },
        staleTime: Infinity,
    });

    const { data: pontosDeEntregaData } = useQuery({
        queryKey: ['listPontosDeEntrega'],
        queryFn: ({ queryKey }) => {
            const [_key, parametro] = queryKey;
            return carregaPontosEntrega({ isFilter: true });
        },
        staleTime: Infinity,
    });

    const handleUpdateStatus = useMutation({
        mutationFn: (data) => atualizaEquipamento(data.id, data.dataToUpdate),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKey });
            // get from cached data, descobrir forma de encontrar qual item foi atualizado.

            if (typeof variables?.dataToUpdate.status === 'boolean') {
                const status = variables?.dataToUpdate.status ? 'ativado' : 'inativado';
                exibirAlerta('Sucesso', `Equipamento ${status} com sucesso`, 'success');
            }
        },
        onError: (error) => {
            const message = error.response?.data?.message ?? 'Erro ao executar operação';
            exibirAlerta('Erro', message, 'error');
        }
    });

    const { mutateAsync: handleGenerateUpdateToken, isPending: isLoadingUpdateToken } = useMutation({
        mutationFn: async (data) => await generateUpdateTokenEquipamento(data.id),
        onSuccess: (response) => {
            // get token from response
            const token = response?.data?.token;
            if (token) {
                setSelectedEquipment((prevState) => ({ ...prevState, token }));
                setOpenGenerateTokenDialog(true);
            }
        },
        onError: (error) => {
            const message = error.response?.data?.message ?? 'Erro ao gerar token';
            exibirAlerta('Erro', message, 'error');
        }
    });

    const handleEdit = (equipamento) => {
        setSelectedEquipment(equipamento);
        setOpenDeskForm(true);
    };

    const handleOpenDeleteDialog = (item) => {
        setSelectedEquipment(item);
        setOpenDeleteDialog(true);
    };

    const handleOpenConfirmGenerateTokenDialog = (item) => {
        setSelectedEquipment(item);
        setOpenConfirmGenerateTokenDialog(true);
    };

    const handleOpenGenerateTokenDialog = async (item) => {
        try {
            exibirDialog('Gerando token. Aguarde...');
            setSelectedEquipment(item);
            setOpenConfirmGenerateTokenDialog(false);
            // endpoint generate token
            await handleGenerateUpdateToken({ id: item.id });
        } catch (error) {
            console.error('ListOfEquipment ~ error:', error);
        } finally {
            handleCloseDialog();
        }
    };

    const { mutateAsync: handleDelete } = useMutation({
        mutationFn: deletaEquipamento,
        onSuccess: (response, id) => {
            const message = response.data.message ?? 'Equipamento excluído com sucesso';
            exibirAlerta('Sucesso', message, 'success');

            if (message === 'O equipamento indicado(a) possui relação com outros itens. Por isso foi inativado') {
                // como o equipamento não é excluído dependendo dos seus relacionamentos,
                // nao é possível saber se excluiu ou se atualizou o status.
                queryClient.invalidateQueries({ queryKey: queryKey });
            } else {
                const cached = queryClient.getQueryData(queryKey);
                const updatedData = {
                    ...cached,
                    data: {
                        ...cached.data,
                        data: cached.data.data.filter((item) => item.id !== id),
                    },
                };
                queryClient.setQueryData(queryKey, updatedData);
            }
        },
        onError: (error) => {
            const message = error.response?.data?.message ?? 'Erro ao executar operação';
            exibirAlerta('Erro', message, 'error');
        },
        onSettled: () => {
            setOpenDeleteDialog(false);
            setSelectedEquipment(null);
        },
    });

    useEffect(() => {
        checkPermissionsAndRedirect(['admin', 'admin_equipamentos', 'list_equipamentos']);

        setSearchParams((state) => {
            state.set('page', String(page + 1));
            state.set('perPage', String(rowsPerPage));
            return state;
        });
    }, [page, rowsPerPage, setSearchParams]);

    useEffect(() => {
        if (openDeskForm) return;
        setSelectedEquipment(null);

        const queryFilters = { ...Object.fromEntries(searchParams) }

        const tableFilters = { ...queryFilters };
        delete tableFilters.page;
        delete tableFilters.perPage;

        const activeFilters = Object.keys(tableFilters).length;

        setActiveFilters(activeFilters);
        setIsOpenFilters(activeFilters > 0);
    }, [openDeskForm, searchParams]);

    useEffect(() => {
        if (isSuccess && !isRefetching) {
            const date = new Date();
            const formattedDate = date.toLocaleString('pt-BR');
            setLastUpdated(formattedDate);
        }
    }, [isSuccess, isRefetching]);

    return (
        <>
            <Helmet title={tipoPlural} defer={false} />

            <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="end"
                spacing={3}
            >
                <Grid item xs={12} md={9}>
                    <Breadcrumbs>
                        <LinkNavegacao to="/"><Home fontSize="small" /></LinkNavegacao>
                        <LinkNavegacao to="/epis">Gestão de EPI's</LinkNavegacao>
                        <LinkNavegacao to="/epis/equipamentos">Equipamentos</LinkNavegacao>
                        <Typography variant='span'>{tipoPlural}</Typography>
                    </Breadcrumbs>
                    <PageTitle
                        icon={equipmentIcon[type]}
                        title={tipoPlural}
                        description={(dadosUsuario.isSempher ? "Gerencie" : "Visualize") + " os " + tipoPlural + " das empresas."}
                    />

                    <Grid item xs={12}>
                        <Stack direction='row' spacing={0.5} alignItems='center' p sx={{ pt: 0 }}>
                            <Typography variant='caption' color='textSecondary'>Última atualização: </Typography>
                            {isLoadingList || lastUpdated === null ? (
                                <Skeleton width={110} variant='text' />
                            ) : (
                                <Typography variant='caption' color='textSecondary'>
                                    {lastUpdated}
                                </Typography>
                            )}
                        </Stack>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Stack direction={{ xs: 'column-reverse', md: 'row' }} spacing={2} justifyContent={'space-between'}>
                        <FiltersButton
                            isOpenFilters={isOpenFilters}
                            setIsOpenFilters={setIsOpenFilters}
                            activeFilters={activeFilters}
                        />

                        {hasPermission(["admin", "admin_equipamentos", "create_equipamentos"]) && dadosUsuario.isSempher &&
                            <Typography sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                                <Button
                                    variant="contained"
                                    startIcon={<Add />}
                                    onClick={() => setOpenDeskForm(true)}
                                    disabled={!hasPermission(["admin", "admin_equipamentos", "create_equipamentos"])}
                                >
                                    Cadastrar {tipo}
                                </Button>
                            </Typography>
                        }
                    </Stack>
                </Grid>

                <EquipmentsTableFilter
                    isOpenFilters={isOpenFilters}
                    isLoadingList={isLoadingList}
                    empresas={empresasData?.data?.data || []}
                    pontosDeEntrega={pontosDeEntregaData?.data?.data || []}
                />

                <Grid item xs={12}>
                    <TableContainer component={Paper}>
                        <CustomToolbarExportTable
                            rows={equipamentos}
                            columns={columns}
                            reportName={"relatorio_" + tipoPlural}
                            reportPdfTitle={"RELATÓRIO DE " + tipoPlural}
                            getDataWithoutPaginate={() => {
                                const { limit, offset, ...data } = queryFilters;
                                return carregaEquipamentos(data);
                            }}
                            getSelectedEmpresa={() => empresasData.data.data.find(empresa => empresa.id === Number(searchParams.get('id_empresas')))}
                        />

                        <Table aria-label={tipoPlural}>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center">Tipo</TableCell>
                                    <TableCell>Nome</TableCell>
                                    <TableCell>Tag</TableCell>
                                    <TableCell>Empresa</TableCell>
                                    <TableCell>Descrição</TableCell>
                                    {type === 'balcao' && (<TableCell>Conexão</TableCell>)}
                                    <TableCell>Última Sincronização</TableCell>
                                    <TableCell>Status</TableCell>
                                    {type === 'balcao' && (
                                        <>
                                            <TableCell align="center">Habilita facial</TableCell>
                                            <TableCell align="center">Habilita retirada</TableCell>
                                        </>
                                    )}
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoadingList ? (
                                    <SkeletonTableRow
                                        numCells={10}
                                        numRows={rowsPerPage < 20 ? rowsPerPage : 5}
                                        firstCellIsIcon
                                    />
                                ) : equipamentos.length > 0 ? (
                                    equipamentos.map((equipamento) => (
                                        <DeskRow
                                            key={equipamento.id}
                                            data={equipamento}
                                            isSempher={dadosUsuario?.isSempher ?? false}
                                            handleOpenEditForm={() => {
                                                setOpenDeskForm(true);
                                                setSelectedEquipment(equipamento)
                                            }}
                                            handleUpdateStatus={(field, value) => {
                                                const dataSendUpdate = {
                                                    id: equipamento.id,
                                                    dataToUpdate: {
                                                        [field]: value
                                                    }
                                                }
                                                handleUpdateStatus.mutateAsync(dataSendUpdate)
                                            }}
                                            handleOpenDeleteDialog={() => handleOpenDeleteDialog(equipamento)}
                                            handleOpenConfirmGenerateTokenDialog={() => handleOpenConfirmGenerateTokenDialog(equipamento)}
                                        />
                                    ))
                                ) : (
                                    <EmptyTableRow colSpan={10} />
                                )}
                            </TableBody>
                        </Table>

                        <Pagination
                            page={page}
                            rowsPerPage={rowsPerPage}
                            numTotalItems={numTotalItems}
                        />
                    </TableContainer>
                </Grid>
            </Grid>

            <DeskForm
                open={openDeskForm}
                setOpen={setOpenDeskForm}
                equipamento={selectedEquipment}
                empresas={empresasData?.data?.data ?? []}
                queryKey={queryKey}
                tipo={tipo}
                type={type}
            />

            <ConfirmDialog
                title={"Excluir " + tipo}
                description={"Tem certeza que deseja excluir este " + tipo + '?'}
                goAction={() => handleDelete(selectedEquipment.id)}
                handleClose={() => {
                    setOpenDeleteDialog(false);
                    setSelectedEquipment(null);
                }}
                state={openDeleteDialog}
            />

            <ConfirmGenerateTokenDialog
                open={openConfirmGenerateTokenDialog}
                selectedEquipment={selectedEquipment}
                goAction={() => handleOpenGenerateTokenDialog(selectedEquipment)}
                handleClose={() => {
                    setOpenConfirmGenerateTokenDialog(false);
                    setSelectedEquipment(null);
                }}
                isLoading={isLoadingUpdateToken}
            />

            <GenerateTokenDialog
                open={openGenerateTokenDialog}
                selectedEquipment={selectedEquipment}
                handleClose={() => {
                    setOpenGenerateTokenDialog(false);
                    setSelectedEquipment(null);
                }}
            />
        </>
    );
}